import { useState, useMemo } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { FileText, Search, Filter, PieChart } from 'lucide-react'
import { Input } from '@/shared/components/ui/input'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { Skeleton } from '@/shared/components/ui/skeleton'
import { useReportesProgrammatic } from '../hooks/useProgrammatic'
import { ProgrammaticCard } from '../components/ProgrammaticCard'
import { useAuth } from '@/auth/context/AuthContext'
import { useTeam } from '@/shared/context/TeamContext'

function toSlug(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '')
}

export function ReportesProgrammaticPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [search, setSearch] = useState('')

  const isSectionAdmin = useMemo(() => {
    if (!user) return false
    return user.is_superuser || user.groups.includes('admin') || user.groups.includes('Programmatic-admin')
  }, [user])

  // Filtrar por área seleccionada
  const { selectedArea, selectedTeam } = useTeam()
  const isClientArea = selectedTeam !== 'Admin' && selectedTeam !== 'Tonal'
  const areaGrupoId = isClientArea ? selectedArea?.laboratorio_id : undefined

  const { data, isLoading, error } = useReportesProgrammatic({
    page: 1,
    page_size: 500,
    search: search || undefined,
    grupo_id: areaGrupoId ?? undefined,
  })

  // Nombre del laboratorio y reportes filtrados por slug
  const { grupoNombre, reportesFiltrados } = useMemo(() => {
    if (!data?.results || !slug) return { grupoNombre: '', reportesFiltrados: [] }

    const filtrados = data.results.filter(r => toSlug(r.grupo_nombre) === slug && r.activo)
    const nombre = filtrados.length > 0 ? filtrados[0].grupo_nombre : slug

    return { grupoNombre: nombre, reportesFiltrados: filtrados }
  }, [data, slug])

  if (error) {
    return (
      <div className="p-8">
        <div className="max-w-6xl mx-auto">
          <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-6 text-center">
            <FileText className="mx-auto h-12 w-12 text-destructive mb-4" />
            <h3 className="text-lg font-semibold text-destructive mb-2">Error al cargar reportes</h3>
            <p className="text-sm text-muted-foreground">
              {(error as any)?.response?.data?.error ||
                'No se pudieron cargar los reportes. Por favor, intenta nuevamente.'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-center gap-3">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <PieChart className="h-6 w-6" />
            {grupoNombre || 'Programmatic'}
          </h1>
          {reportesFiltrados.length > 0 && (
            <Badge className="badge-gradient text-xs inline-flex items-center gap-1.5">
              <FileText className="h-3 w-3" />
              {reportesFiltrados.length} {reportesFiltrados.length === 1 ? 'reporte disponible' : 'reportes disponibles'}
            </Badge>
          )}
        </div>
        {isSectionAdmin && (
          <Button onClick={() => navigate('/programmatic/configuracion')} className="bg-blue-800 hover:bg-blue-900 text-white">
            <Filter className="mr-2 h-4 w-4" />
            Configuración
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="flex flex-col gap-3 md:flex-row md:items-center">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar reportes por nombre..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="space-y-6">
          {[...Array(2)].map((_, i) => (
            <div key={i} className="space-y-3">
              <Skeleton className="h-7 w-48" />
              <div className="grid gap-4 md:grid-cols-2">
                <Skeleton className="h-64 w-full" />
                <Skeleton className="h-64 w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : reportesFiltrados.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {reportesFiltrados.map((reporte) => (
            <ProgrammaticCard key={reporte.id} reporte={reporte} />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <PieChart className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <h3 className="text-lg font-semibold mb-2">No se encontraron reportes</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {search
              ? 'No hay reportes que coincidan con la búsqueda.'
              : 'No tienes reportes asignados para este laboratorio.'}
          </p>
          {isSectionAdmin && (
            <Button onClick={() => navigate('/programmatic/configuracion')} className="bg-blue-800 hover:bg-blue-900 text-white">
              <Filter className="mr-2 h-4 w-4" />
              Ir a Configuración
            </Button>
          )}
        </div>
      )}
    </div>
  )
}
